import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getPublicProfile, getPublicGarageVehicles } from '../../lib/supabase'
import { calculateVehicleScore, getScoreTier } from '../../lib/garageScore'
import {
  Car,
  ArrowLeft,
  Calendar,
  AlertCircle,
  Lock,
  Award,
  Gauge,
  Zap,
} from 'lucide-react'
import { format } from 'date-fns'

// Format display name: first name + last initial (e.g., "Michael R.")
const formatDisplayName = (fullName) => {
  if (!fullName) return 'User'
  const parts = fullName.trim().split(' ')
  const firstName = parts[0]
  const lastName = parts[parts.length - 1]
  return `${firstName} ${lastName.charAt(0)}.`
}

// Generate initials for avatar placeholder
const getInitials = (fullName) => {
  if (!fullName) return 'U'
  const parts = fullName.trim().split(' ')
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

// Profile avatar
const ProfileAvatar = ({ name, avatarUrl }) => {
  const initials = getInitials(name)

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
      />
    )
  }

  return (
    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-rdc-primary to-rdc-primary-dark flex items-center justify-center border-4 border-white shadow-lg">
      <span className="text-2xl font-bold text-white">{initials}</span>
    </div>
  )
}

// Tier badge for provenance score
const TierBadge = ({ tier }) => {
  return (
    <div className={`${tier.bgClass} ${tier.textClass} rounded-lg px-4 py-3 border ${tier.borderClass}`}>
      <div className="flex items-center gap-2">
        <Award size={16} className="flex-shrink-0" />
        <div>
          <div className="text-xs font-medium opacity-75">Provenance Score Tier</div>
          <div className="text-sm font-bold">{tier.name}</div>
        </div>
      </div>
    </div>
  )
}

// Vehicle card for public showcase
const VehicleCard = ({ vehicle }) => {
  const handleClick = () => {
    window.location.href = `/v/${vehicle.id}`
  }

  return (
    <button
      onClick={handleClick}
      className="card overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group text-left w-full"
    >
      <div className="h-48 bg-rdc-cream overflow-hidden relative">
        {vehicle.primary_image_url ? (
          <img
            src={vehicle.primary_image_url}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-rdc-cream to-rdc-warm-gray">
            <Car size={48} className="text-rdc-dark-gray opacity-40" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="text-xs text-rdc-taupe mb-1 font-medium">{vehicle.year}</div>
        <h3 className="font-display text-lg font-semibold text-black mb-1 group-hover:text-rdc-primary transition-colors">
          {vehicle.make} {vehicle.model}
        </h3>
        {vehicle.color && (
          <div className="text-sm text-rdc-dark-gray">{vehicle.color}</div>
        )}
        {vehicle.mileage !== null && (
          <div className="text-xs text-rdc-taupe mt-2 flex items-center gap-1">
            <Gauge size={12} />
            {vehicle.mileage.toLocaleString()} miles
          </div>
        )}
      </div>
    </button>
  )
}

// Private profile message
const PrivateProfileMessage = () => (
  <div className="min-h-screen bg-rdc-cream flex items-center justify-center p-4">
    <div className="card max-w-md w-full p-8 text-center">
      <div className="w-12 h-12 rounded-full bg-rdc-tan/20 flex items-center justify-center mx-auto mb-4">
        <Lock size={24} className="text-rdc-tan" />
      </div>
      <h1 className="text-2xl font-display font-semibold text-black mb-2">Profile is Private</h1>
      <p className="text-rdc-taupe mb-6">
        This profile is not publicly available. The user can enable public profile sharing in their account settings.
      </p>
      <Link
        to="/"
        className="btn-primary inline-block"
      >
        Back to Digital Garage
      </Link>
    </div>
  </div>
)

// Loading state
const LoadingSpinner = () => (
  <div className="min-h-screen bg-rdc-cream flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-rdc-primary border-t-transparent mx-auto mb-4"></div>
      <p className="text-rdc-taupe">Loading profile...</p>
    </div>
  </div>
)

export default function UserProfile() {
  const { userId } = useParams()
  const [profile, setProfile] = useState(null)
  const [vehicles, setVehicles] = useState([])
  const [collectionStats, setCollectionStats] = useState(null)
  const [collectionTier, setCollectionTier] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    loadData()
  }, [userId])

  const loadData = async () => {
    try {
      setLoading(true)
      setError('')

      // Get public profile
      const profileRes = await getPublicProfile(userId)
      if (profileRes.error || !profileRes.data) {
        setError('private_profile')
        setLoading(false)
        return
      }

      setProfile(profileRes.data)

      // Get public vehicles
      const vehiclesRes = await getPublicGarageVehicles(userId)
      if (vehiclesRes.error) throw vehiclesRes.error

      const vehicleList = vehiclesRes.data || []
      setVehicles(vehicleList)

      // Calculate collection statistics
      if (vehicleList.length > 0) {
        // For simplicity, we'll calculate based on vehicle count
        // In a real app, you might want to fetch service record counts per vehicle
        const stats = {
          vehicleCount: vehicleList.length,
          serviceRecordsTotal: 0, // Could aggregate from vehicles if that data was available
        }
        setCollectionStats(stats)

        // Determine collection tier based on vehicle count and documentation
        // This is a simplified tier - in production you'd calculate based on actual scores
        let tierIndex = 0
        if (vehicleList.length >= 5) tierIndex = 2
        if (vehicleList.length >= 10) tierIndex = 3
        if (vehicleList.length >= 15) tierIndex = 4

        // Import SCORE_TIERS if needed for tier calculation
        const SCORE_TIERS = [
          { name: 'Barn Find', color: 'rdc-gray', bgClass: 'bg-gray-100', textClass: 'text-gray-700', borderClass: 'border-gray-300', icon: 'barn' },
          { name: 'Daily Driver', color: 'rdc-blue', bgClass: 'bg-blue-100', textClass: 'text-blue-700', borderClass: 'border-blue-300', icon: 'road' },
          { name: 'Show Ready', color: 'rdc-forest', bgClass: 'bg-green-100', textClass: 'text-green-700', borderClass: 'border-green-300', icon: 'trophy' },
          { name: 'Concours', color: 'rdc-gold', bgClass: 'bg-amber-100', textClass: 'text-amber-700', borderClass: 'border-amber-300', icon: 'medal' },
          { name: 'Best in Show', color: 'rdc-orange', bgClass: 'bg-orange-100', textClass: 'text-orange-700', borderClass: 'border-orange-300', icon: 'crown' },
        ]
        setCollectionTier(SCORE_TIERS[Math.min(tierIndex, SCORE_TIERS.length - 1)])
      }
    } catch (err) {
      console.error('Error loading profile:', err)
      setError('load_failed')
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <LoadingSpinner />
  if (error === 'private_profile') return <PrivateProfileMessage />

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-rdc-cream flex items-center justify-center p-4">
        <div className="card max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={24} className="text-red-600" />
          </div>
          <h1 className="text-xl font-display font-semibold text-black mb-2">Profile Not Found</h1>
          <p className="text-rdc-taupe mb-6">We couldn't load this profile. Please check the URL and try again.</p>
          <Link to="/" className="btn-primary inline-block">
            Back to Digital Garage
          </Link>
        </div>
      </div>
    )
  }

  const displayName = formatDisplayName(profile.full_name)
  const memberSince = profile.member_since
    ? format(new Date(profile.member_since), 'MMMM yyyy')
    : null

  return (
    <div className="min-h-screen bg-rdc-cream">
      {/* Header Navigation */}
      <div className="bg-white border-b border-rdc-cream sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-rdc-primary hover:text-rdc-primary-dark transition-colors text-sm font-medium"
          >
            <ArrowLeft size={20} />
            <span>Back to Digital Garage</span>
          </Link>
        </div>
      </div>

      {/* Profile Header */}
      <div className="bg-white border-b border-rdc-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6 mb-8">
            {/* Avatar */}
            <ProfileAvatar
              name={profile.full_name}
              avatarUrl={profile.avatar_url}
            />

            {/* Profile Info */}
            <div className="flex-1">
              <h1 className="text-4xl font-display font-bold text-black mb-2">
                {displayName}
              </h1>

              {/* Member Since */}
              {memberSince && (
                <div className="flex items-center gap-2 text-rdc-taupe mb-4">
                  <Calendar size={16} />
                  <span className="text-sm">Member since {memberSince}</span>
                </div>
              )}

              {/* Company Name if available */}
              {profile.company_name && (
                <p className="text-rdc-dark-gray font-medium">{profile.company_name}</p>
              )}
            </div>
          </div>

          {/* Collection Tier Badge */}
          {collectionTier && (
            <TierBadge tier={collectionTier} />
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Collection Statistics */}
        {collectionStats && (
          <div className="mb-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="card p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Car size={20} className="text-rdc-primary" />
                  <div className="text-xs text-rdc-taupe font-medium">Total Vehicles</div>
                </div>
                <div className="text-3xl font-display font-bold text-black">
                  {collectionStats.vehicleCount}
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Zap size={20} className="text-rdc-primary" />
                  <div className="text-xs text-rdc-taupe font-medium">Documented</div>
                </div>
                <div className="text-3xl font-display font-bold text-black">
                  {collectionStats.vehicleCount}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Vehicles Section */}
        {vehicles.length === 0 ? (
          <div className="card p-12 text-center">
            <Car size={48} className="text-rdc-warm-gray mx-auto mb-4 opacity-40" />
            <h2 className="text-xl font-display font-semibold text-black mb-2">
              No Public Vehicles
            </h2>
            <p className="text-rdc-taupe">
              {displayName} hasn't shared any vehicles yet.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-display font-semibold text-black mb-2">
                Collection
              </h2>
              <p className="text-rdc-taupe">
                {vehicles.length} {vehicles.length === 1 ? 'vehicle' : 'vehicles'} publicly shared
              </p>
            </div>

            {/* Vehicle Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {vehicles.map((vehicle) => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={vehicle}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Privacy Notice Footer */}
      <div className="bg-white border-t border-rdc-cream py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-rdc-cream/50 border border-rdc-cream rounded-lg p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Lock size={16} className="text-rdc-taupe" />
              <span className="text-sm font-medium text-rdc-taupe">Privacy First</span>
            </div>
            <p className="text-xs text-rdc-taupe leading-relaxed">
              This profile displays only information {displayName} has chosen to share publicly.
              Personal contact details, exact location, email address, and complete service history
              are kept private for security and privacy reasons.
            </p>
          </div>
        </div>
      </div>

      {/* Powered By Footer */}
      <div className="bg-white border-t border-rdc-cream py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-rdc-taupe">
            Documented on <span className="font-semibold text-rdc-primary">RDC Digital Garage</span>
          </p>
        </div>
      </div>
    </div>
  )
}
